import React from "react";
import PropTypes from "prop-types";
import TextInfo from "@gc-npm/components.text";
import sendGaComponentEvent from "@gc-npm/utils.metrics.send-ga-component-event";
import { composeCssClassNamesAndStyles } from "./tools";
import { SWITCH_TYPE } from "./enums";
const COMPONENT_NAME = "cms-switch";

export { SWITCH_TYPE };

const CmsSwitch = (props) => {
  const { cmsSwitchProps, cmsHandler, className } = props;

  if (!cmsSwitchProps || !cmsSwitchProps.onChange || !cmsSwitchProps.id) {
    return null;
  }

  const {
    onChange,
    selectedState,
    disabled = false,
    tagginInfo,
    id,
    inputName,
    ariaLabelledby,
    type,
  } = cmsSwitchProps;
  const cmsTextLabel = props["cms-atom-text-1"];

  const isOn = !!selectedState;

  const toggle = () => {
    if (disabled) return;
    onChange?.(!isOn);
    if(!tagginInfo?.debug) {
    sendGaComponentEvent({
      componentProduct: cmsTextLabel && cmsTextLabel?.text,
      componentName: COMPONENT_NAME,
      componentContent: isOn ? "off" : "on",
      componentVariation: "All",
      eventType: "click",
      ...tagginInfo,
    });
    }
    
  };

  const { cssClassNames, switchStyles, switchThumbStyles } =
    composeCssClassNamesAndStyles({
      type,
      isOn,
      disabled,
      className,
      cmsHandler,
    });

  return (
    <label
      htmlFor={id}
      className={`switch-wrapper ${className} ${disabled ? "disabled" : ""}`}
    >
      <input
        type="checkbox"
        checked={isOn}
        onChange={toggle}
        role="switch"
        aria-checked={isOn}
        disabled={disabled}
        aria-disabled={disabled}
        id={id}
        name={inputName}
        aria-labelledby={ariaLabelledby}
      />
      <span
        aria-labelledby={id}
        style={switchStyles}
        className={`${cssClassNames} switch-visual ${disabled && "skeleton"}`}
        aria-hidden="true"
      >
        <span
          style={switchThumbStyles}
          className={`switch-thumb ${
            isOn ? "switch-thumb--on" : "switch-thumb--off"
          }`}
        />
      </span>
      {cmsTextLabel && (
        <TextInfo
          {...cmsTextLabel}
          className={disabled && "skeleton"}
          cmsHandler={cmsHandler}
        />
      )}

      <style jsx>{`
        .switch-wrapper {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          user-select: none;
          position: relative;
        }

        .switch-wrapper.disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }

        .switch-visual {
          position: relative;
          display: inline-block;
          width: 44px;
          height: 24px;
          border-radius: 9999px;
          background: #cfcfd3;
          transition: background 180ms ease, box-shadow 180ms ease;
          box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.06);
          flex-shrink: 0;
        }

        .switch-thumb {
          position: absolute;
          top: 2px;
          left: 2px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #ffffff;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.24);
          transition: transform 180ms ease, left 180ms ease;
        }

        .switch-thumb--on {
          transform: translateX(20px);
        }
        .switch-thumb--off {
          transform: translateX(0);
        }

        input[type="checkbox"] {
          position: absolute;
          opacity: 0;
          pointer-events: none;
          width: 0;
          height: 0;
          margin: 0;
        }

        input[type="checkbox"]:focus-visible + .switch-visual {
          outline: 2px solid #0066ff;
          outline-offset: 3px;
        }

        :global(.switch-visual.switch-on),
        :global(.is-on .switch-visual) {
          background: #22c55e;
        }

        .skeleton {
          position: relative;
          overflow: hidden;
        }
        .skeleton::after {
          content: "";
          position: absolute;
          inset: 0;
          transform: translateX(-100%);
          background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.35) 50%,
            rgba(255, 255, 255, 0) 100%
          );
          animation: shimmer 1.2s infinite;
        }
        @keyframes shimmer {
          100% {
            transform: translateX(100%);
          }
        }

        :global(.switch-wrapper .PRIMARY.switch-visual) {
          background: #2563eb;
        }
        :global(.switch-wrapper .SECONDARY.switch-visual) {
          background: #6b7280;
        }
        :global(.switch-wrapper .SUCCESS.switch-visual) {
          background: #16a34a;
        }
        :global(.switch-wrapper .WARNING.switch-visual) {
          background: #f59e0b;
        }
        :global(.switch-wrapper .DANGER.switch-visual) {
          background: #dc2626;
        }

        .disabled .switch-visual {
          background: #e5e7eb;
          box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.05);
        }
      `}</style>
    </label>
  );
};

CmsSwitch.propTypes = {
  cmsSwitchProps: PropTypes.shape({
    onChange: PropTypes.func.isRequired,
    selectedState: PropTypes.bool,
    disabled: PropTypes.bool,
    id: PropTypes.string.isRequired,
    inputName: PropTypes.string,
    ariaLabelledby: PropTypes.string,
    type: PropTypes.string,
    tagginInfo: PropTypes.object,
  }),
  cmsHandler: PropTypes.func,
  className: PropTypes.string,
  "cms-atom-text-1": PropTypes.func,
};

CmsSwitch.defaultProps = {
  cmsSwitchProps: {
    selectedState: false,
    disabled: false,
    inputName: "cms-switch-1",
    type: "PRIMARY",
  },
  className: "",
};

export default CmsSwitch;
